import React, {
  Children,
  type MouseEvent,
  ReactElement,
  ReactNode,
  cloneElement,
  forwardRef,
  isValidElement,
  useCallback,
  useRef,
  useState,
} from "react";

import {
  BottomNavigationAction,
  BottomNavigationActionProps,
  Menu,
  MenuProps,
  Tooltip,
  TooltipProps,
} from "@mui/material";

import { useLongPress } from "@/hooks";

const NAME = "BottomNavigationActionMenu";

export interface BottomNavigationActionMenuProps extends BottomNavigationActionProps {
  menu: ReactNode;
  menuProps?: Omit<MenuProps, "children" | "open">;
  tooltip?: ReactNode;
  tooltipProps?: Omit<TooltipProps, "title">;
}

export const BottomNavigationActionMenu = forwardRef<
  HTMLButtonElement,
  BottomNavigationActionMenuProps
>(
  (
    {
      onClick,
      tooltip,
      tooltipProps,
      menu,
      menuProps,
      // eslint-disable-next-line
      component, // Extract but don't use - prevents RouterLink from handling clicks
      ...BottomNavigationActionProps
    },
    externalRef,
  ) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const internalRef = useRef<HTMLButtonElement>(null);

    // Combine external and internal refs
    const buttonRef = useCallback(
      (node: HTMLButtonElement | null) => {
        // Update internal ref
        if (internalRef.current !== node) {
          (
            internalRef as React.MutableRefObject<HTMLButtonElement | null>
          ).current = node;
        }

        // Update external ref if provided
        if (typeof externalRef === "function") {
          externalRef(node);
        } else if (externalRef && node !== null) {
          (
            externalRef as React.MutableRefObject<HTMLButtonElement | null>
          ).current = node;
        }
      },
      [externalRef],
    );

    const handleClose = useCallback(() => {
      setAnchorEl(null);
    }, []);

    const handleLongPress = useCallback((element: HTMLButtonElement) => {
      setAnchorEl(element);
    }, []);

    // Use long press hook
    const longPressHandlers = useLongPress<HTMLButtonElement>({
      onLongPress: handleLongPress,
      onClick,
      delay: 500,
    });

    const open = Boolean(anchorEl);

    return (
      <>
        {tooltip ? (
          <Tooltip title={tooltip} {...tooltipProps}>
            <BottomNavigationAction
              {...longPressHandlers}
              ref={buttonRef}
              {...BottomNavigationActionProps}
            />
          </Tooltip>
        ) : (
          <BottomNavigationAction
            {...longPressHandlers}
            ref={buttonRef}
            {...BottomNavigationActionProps}
          />
        )}
        <Menu
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          onClose={handleClose}
          open={open}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          {...menuProps}
        >
          {(() => {
            const processChildren = (children: ReactNode): ReactNode => {
              return Children.map(children, (child) => {
                if (!isValidElement(child)) return child;

                // Check if child is a Fragment
                if (
                  child.type === React.Fragment ||
                  child.type.toString() === "Symbol(react.fragment)"
                ) {
                  // Recursively process fragment children
                  return cloneElement(child as ReactElement, {
                    children: processChildren(child.props.children),
                  });
                }

                // For regular elements, add onClick handler
                return cloneElement(child as ReactElement, {
                  onClick: (e: MouseEvent<HTMLElement>) => {
                    handleClose();
                    if (child.props.onClick) child.props.onClick(e);
                  },
                });
              });
            };

            return processChildren(menu);
          })()}
        </Menu>
      </>
    );
  },
);
BottomNavigationActionMenu.displayName = NAME;
